import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { getTeamInfoById } from '../utils/getTeamInfo';
import apiClient from '../lib/api/apiClient';
import dayjs from 'dayjs';
import BroadcastGame, { type BroadcastGameRow } from './BroadcastGame';

interface GameDetailedResponse {
  sections: Array<{ title: string; columns: string[]; rows: Record<string, unknown>[] }>;
}

interface PlayByPlayModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: number | null;
  gameRow: BroadcastGameRow | null;
  league: {
    conferences?: Array<{
      divisions?: Array<{
        teams?: Array<{
          id: number;
          name?: string;
          logo?: string;
          color_primary?: string;
          color_secondary?: string;
          color_text?: string;
        }>;
      }>;
    }>;
  } | null;
}

function teamAbbr(info: { abbreviation?: string; rw_abbreviation?: string; name?: string } | null): string {
  if (!info) return '—';
  const a = info.rw_abbreviation || info.abbreviation;
  if (a) return a.toUpperCase();
  const n = (info.name || '').trim();
  if (n.length <= 5) return n.toUpperCase();
  return n
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();
}

/** Full static game recap using broadcast feed styling (all rows rendered immediately). */
const PlayByPlayModal: React.FC<PlayByPlayModalProps> = ({ isOpen, onClose, gameId, gameRow, league }) => {
  const modalBg = '#0a0d12';

  const { data: detailedData, isLoading } = useQuery<GameDetailedResponse>({
    queryKey: ['gameDetailed', gameId],
    queryFn: async () => {
      const res = await apiClient.get(`/games/scores/game/${gameId}/detailed`);
      return res.data;
    },
    enabled: isOpen && !!gameId,
  });

  const playByPlaySection = detailedData?.sections?.find((s) => s.title === 'PlayByPlay');
  const playByPlayRows = playByPlaySection?.rows ?? [];
  const announcementKey = playByPlaySection?.columns?.includes('announcement') ? 'announcement' : 'play_text';

  if (!gameRow || !league) {
    return null;
  }

  const awayInfo = getTeamInfoById(gameRow.away_team_id, league as Parameters<typeof getTeamInfoById>[1]);
  const homeInfo = getTeamInfoById(gameRow.home_team_id, league as Parameters<typeof getTeamInfoById>[1]);
  const awayName = awayInfo?.name ?? `Team ${gameRow.away_team_id}`;
  const homeName = homeInfo?.name ?? `Team ${gameRow.home_team_id}`;
  const recapDateLabel = gameRow.scheduled_date ? dayjs(gameRow.scheduled_date).format('MMM D, YYYY') : '—';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent
        maxW="75vw"
        maxH="80vh"
        h="80vh"
        w="75vw"
        bg={modalBg}
        borderRadius="md"
        borderWidth="1px"
        borderColor="whiteAlpha.200"
      >
        <ModalCloseButton color="gray.400" _hover={{ color: 'white' }} />
        <ModalBody p={4} display="flex" flexDir="column" minH={0}>
          <Box flex={1} minH={0}>
            <BroadcastGame
              isActive={isOpen}
              isLoading={isLoading}
              isPaused
              playbackSpeed={0.5}
              replayNonce={0}
              playbackComplete
              feedOnly
              showAllRows
              isRecapView
              recapDateLabel={recapDateLabel}
              playByPlayRows={playByPlayRows}
              announcementKey={announcementKey}
              gameRow={gameRow}
              awayTeam={{
                name: awayName,
                abbr: teamAbbr(awayInfo as Parameters<typeof teamAbbr>[0]),
                logo: awayInfo?.logo,
                color_primary: awayInfo?.color_primary,
                color_secondary: awayInfo?.color_secondary,
                color_text: awayInfo?.color_text,
              }}
              homeTeam={{
                name: homeName,
                abbr: teamAbbr(homeInfo as Parameters<typeof teamAbbr>[0]),
                logo: homeInfo?.logo,
                color_primary: homeInfo?.color_primary,
                color_secondary: homeInfo?.color_secondary,
                color_text: homeInfo?.color_text,
              }}
            />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PlayByPlayModal;
